// ./src/config/branding.ts
// ============================================
// Multi-brand STYLE + deployment configuration.
//
// A single deployment serves every first-party domain, so the active style is
// resolved at RUNTIME from the hostname. Locally there's no branded hostname,
// so VITE_SPACE_ID picks it.
//
// Only the brand's STYLE (theme variant, tint) lives here; its identity — name,
// logo, contacts — is the white label's (BrandingContext). *Deployment* values
// come from env via a brand-aware resolver.
// ============================================

import type { ThemeVariant } from '@em/ui';
import { sameSiteLocalEndpoint } from '@/utils/localEndpoint';

export type BrandId = 'eventsmagic' | 'partylabz';
export type { ThemeVariant };

const env = import.meta.env as Record<string, string | undefined>;

const RAW_SPACE_ID = (env.VITE_SPACE_ID || '').trim();

/** Map a hostname to a first-party brand (null if not recognised). */
export const detectBrandFromHost = (host?: string): BrandId | null => {
  const h = (host ?? (typeof window !== 'undefined' ? window.location.hostname : '')).toLowerCase();
  if (!h) return null;
  if (h.includes('partylabz')) return 'partylabz';
  if (h.includes('eventsmagic')) return 'eventsmagic';
  return null;
};

/**
 * Resolve the active brand. The hostname wins (one deployment, two domains);
 * on localhost / unknown hosts we fall back to VITE_SPACE_ID.
 */
export const resolveBrandId = (): BrandId => {
  const fromHost = detectBrandFromHost();
  if (fromHost) return fromHost;
  return RAW_SPACE_ID.toLowerCase().includes('party') ? 'partylabz' : 'eventsmagic';
};

export const BRAND_ID: BrandId = resolveBrandId();

/**
 * Brand-aware env lookup. Prefers VITE_<BRAND>_<KEY>, falls back to VITE_<KEY>.
 */
export const getEnv = (key: string, fallback = ''): string => {
  const prefixed = env[`VITE_${BRAND_ID.toUpperCase()}_${key}`];
  const plain = env[`VITE_${key}`];
  return (prefixed ?? plain ?? fallback) as string;
};

// ⚠ A2e #101 — NO BRAND IDENTITY HERE. The name, website, support address, logo and icons the user sees come from the
// WHITE LABEL of the host (BrandingContext, utils/appBrand — remembered per host, generic until known); this build only
// picks a STYLE. `check-brand-literals` allows no brand name or domain anywhere in the front end, this file included.
export interface BrandStyle {
  id: BrandId;
  /** Primary market focus — used later to gate B2B-only features. */
  focus: 'b2b_b2c' | 'b2c';
  /** Which local theme variant this brand uses. */
  themeVariant: ThemeVariant;
  /**
   * Browser theme-color / status bar tint — shade 7 of the variant's primary (eventsmagic: iris, from its logo).
   * ⚠ index.html's head script spells the same two values for the first paint (`check-brand-theme`).
   */
  themeColor: string;
}

export const BRAND_STYLES: Record<BrandId, BrandStyle> = {
  eventsmagic: { id: 'eventsmagic', focus: 'b2b_b2c', themeVariant: 'business', themeColor: '#5329a8' },
  partylabz: { id: 'partylabz', focus: 'b2c', themeVariant: 'festive', themeColor: '#f59e0b' },
};

/** The active build's style. */
export const brandStyle: BrandStyle = BRAND_STYLES[BRAND_ID];

const pageHost = typeof window !== 'undefined' ? window.location.hostname : '';
const endpoint = (key: string, fallback = ''): string => sameSiteLocalEndpoint(getEnv(key, fallback), pageHost);

/**
 * Resolved, brand-aware deployment config. Read from here instead of touching
 * import.meta.env directly so the VITE_<BRAND>_* overrides always apply.
 */
export const appEnv = {
  spaceId: getEnv('SPACE_ID') || RAW_SPACE_ID,
  apiUrl: endpoint('API_URL'),
  rootApiUrl: endpoint('ROOT_API_URL'),
  gatewayEndpoint: endpoint('GATEWAY_ENDPOINT'),
  graphqlEndpoint: endpoint('GRAPHQL_ENDPOINT', 'http://localhost:4000/graphql'),
  googleTag: getEnv('GOOGLE_TAG'),
  // ROADMAP #189 — public by design. ⚠ Set only once the gateway that allows `traceparent` is deployed.
  appInsightsConnectionString: getEnv('APPINSIGHTS_CONNECTION_STRING'),
  googleMapsApiKey: getEnv('GOOGLE_MAPS_API_KEY'),
  formSubmitsFormId: getEnv('FORMSUBMITS_FORM_ID'),
  appDomain: getEnv('APP_DOMAIN'),
  appVersion: (env.VITE_APP_VERSION as string) || '',
} as const;
